/**
 * City Agency Landing Pages Data
 * 25 cities across all continents
 * Each config produces a 1500+ word page for "software development agency [city]"
 */

export interface CityAgencyData {
  slug: string;
  cityName: string;
  countryName: string;
  flag: string;
  timezone: string;
  currency: string;
  // SEO
  title: Record<string, string>;
  description: Record<string, string>;
  keywords: string[];
  // Hero
  heroTitle: Record<string, string>;
  heroSubtitle: Record<string, string>;
  // Content
  whyTitle: Record<string, string>;
  whyReasons: { icon: string; title: string; description: string }[];
  services: { name: string; description: string }[];
  industries: { name: string; description: string }[];
  faqs: { question: string; answer: string }[];
  // Pricing
  localRate: string;
  ourRate: string;
  savings: string;
}

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling. React, Node.js, Python, cloud-native.' },
  { name: 'Web Application Development', description: 'Enterprise web applications, portals, dashboards, and internal tools. Modern stack with React, TypeScript, and cloud infrastructure.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter. From MVP to enterprise-grade mobile solutions.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, recommendation engines, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, monitoring, and 24/7 managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists, and project managers integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems. Figma-first approach with accessibility compliance.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, and enterprise security standards aligned with GDPR and industry best practices.' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools, and banking applications with enterprise security standards.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, inventory management, and omnichannel retail solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine platforms, EHR integrations, and HIPAA-compliant health applications.' },
  { name: 'SaaS & Technology', description: 'Product development, scaling, and modernization for SaaS companies from seed to Series C+.' },
  { name: 'Real Estate & PropTech', description: 'Property management platforms, virtual tours, tenant portals, and real estate marketplaces.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse management, and supply chain visibility platforms.' },
];

export const CITY_AGENCY_PAGES: Record<string, CityAgencyData> = {

  casablanca: {
    slug: 'software-development-agency-casablanca',
    cityName: 'Casablanca',
    countryName: 'Morocco',
    flag: '🇲🇦',
    timezone: 'CET (GMT+1)',
    currency: 'MAD/EUR',
    title: {
      en: 'Software Development Agency Casablanca | Call IT Dev',
      fr: 'Agence de Développement Informatique Casablanca | Call IT Dev',
      es: 'Agencia Desarrollo Software Casablanca | Call IT Dev',
      de: 'Softwareentwicklung Agentur Casablanca | Call IT Dev',
      ar: 'وكالة تطوير برمجيات الدار البيضاء | Call IT Dev',
    },
    description: {
      en: 'Top software development agency in Casablanca, Morocco. Custom SaaS, web, mobile, AI development. 200+ engineers, CET timezone, from $25/hr. ISO 27001 certified.',
      fr: 'Agence de développement informatique à Casablanca, Maroc. SaaS, web, mobile, IA. 200+ ingénieurs, fuseau CET, dès 25 $/h. Certifié ISO 27001.',
      es: 'Agencia de desarrollo de software en Casablanca, Marruecos. SaaS, web, móvil, IA. 200+ ingenieros, zona CET, desde 25 $/h.',
      de: 'Top-Softwareentwicklungsagentur in Casablanca, Marokko. SaaS, Web, Mobile, KI. 200+ Ingenieure, CET-Zeitzone, ab 25 $/h.',
      ar: 'أفضل وكالة تطوير برمجيات في الدار البيضاء، المغرب. SaaS، ويب، موبايل، ذكاء اصطناعي. 200+ مهندس.',
    },
    keywords: ['software development agency Casablanca', 'agence développement informatique Casablanca', 'nearshore development Morocco', 'IT outsourcing Casablanca', 'web development Casablanca', 'SaaS development Morocco', 'AI development Casablanca', 'mobile app development Morocco'],
    heroTitle: {
      en: 'Software Development Agency in Casablanca',
      fr: 'Agence de Développement Informatique à Casablanca',
      es: 'Agencia de Desarrollo de Software en Casablanca',
      de: 'Softwareentwicklung Agentur in Casablanca',
      ar: 'وكالة تطوير برمجيات في الدار البيضاء',
    },
    heroSubtitle: {
      en: '200+ engineers, CET timezone, 60% savings vs Western Europe. From SaaS to AI — we build enterprise software at nearshore prices.',
      fr: '200+ ingénieurs, fuseau CET, 60% d\'économies vs Europe. Du SaaS à l\'IA — logiciels d\'entreprise à prix nearshore.',
      es: '200+ ingenieros, zona CET, 60% ahorro vs Europa. De SaaS a IA — software empresarial a precios nearshore.',
      de: '200+ Ingenieure, CET-Zeitzone, 60% Ersparnis vs Westeuropa. Von SaaS bis KI — Enterprise-Software zu Nearshore-Preisen.',
      ar: '200+ مهندس، منطقة CET، توفير 60% مقارنة بأوروبا الغربية. من SaaS إلى الذكاء الاصطناعي.',
    },
    whyTitle: {
      en: 'Why Choose Casablanca for Software Development',
      fr: 'Pourquoi Choisir Casablanca pour le Développement Logiciel',
      es: 'Por Qué Elegir Casablanca para Desarrollo de Software',
      de: 'Warum Casablanca für Softwareentwicklung Wählen',
      ar: 'لماذا تختار الدار البيضاء لتطوير البرمجيات',
    },
    whyReasons: [
      { icon: '🎓', title: 'Top Engineering Talent', description: 'Morocco produces 30K+ STEM graduates annually. Our engineers hold degrees from top Moroccan and European universities.' },
      { icon: '⏰', title: 'CET Timezone Alignment', description: 'Same timezone as Paris, Berlin, Madrid. Real-time collaboration with no jet lag.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior developers at $25-40/hr vs $80-120/hr in Western Europe. Same quality, fraction of the cost.' },
      { icon: '🌍', title: 'Multilingual Teams', description: 'Engineers fluent in French, English, Arabic, and Spanish. Seamless communication with European clients.' },
      { icon: '🏗️', title: 'Tech Hub Infrastructure', description: 'Casablanca Finance City, Technopark, fiber optic connectivity, and government-backed tech incentives.' },
      { icon: '✈️', title: '3-Hour Flight to Europe', description: 'Direct flights to Paris, Madrid, London, Berlin. Easy for onsite visits and team integration.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Casablanca for software development?', answer: 'Casablanca combines top engineering talent, CET timezone alignment, multilingual teams (French/English/Arabic), and 60% cost savings vs Western Europe. It\'s the #1 nearshore destination for European tech companies.' },
      { question: 'What technologies do your Casablanca engineers work with?', answer: 'React, TypeScript, Node.js, Python, Java, .NET, AWS, Azure, GCP, Docker, Kubernetes, TensorFlow, PyTorch. Full-stack and specialized teams available.' },
      { question: 'How do you ensure code quality?', answer: 'Mandatory code reviews, automated CI/CD pipelines, 80%+ test coverage targets, SonarQube static analysis, and bi-weekly sprint demos with your team.' },
      { question: 'Can I visit the Casablanca office?', answer: 'Absolutely! Our modern office is in Casablanca\'s tech district. Direct flights from Paris (3h), Madrid (1.5h), London (3.5h). We welcome client visits anytime.' },
      { question: 'What is the minimum team size?', answer: 'Start with 2 developers and scale to 50+. We offer dedicated teams, staff augmentation, and project-based engagement models.' },
      { question: 'Do you handle data security and compliance?', answer: 'Yes. We follow enterprise security standards, are GDPR compliant, and all engineers sign NDAs. Encrypted communications and secure development environments.' },
    ],
    localRate: '€80-120',
    ourRate: '€25-40',
    savings: '60%+',
  },

  paris: {
    slug: 'software-development-agency-paris',
    cityName: 'Paris',
    countryName: 'France',
    flag: '🇫🇷',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Paris | Call IT Dev',
      fr: 'Agence de Développement Informatique Paris | Call IT Dev',
      es: 'Agencia Desarrollo Software París | Call IT Dev',
      de: 'Softwareentwicklung Agentur Paris | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Paris. Custom SaaS, web, mobile, AI. Native French-speaking engineers, CET timezone, from $25/hr. 3h from Casablanca HQ.',
      fr: 'Agence de développement informatique pour Paris. SaaS sur-mesure, web, mobile, IA. Ingénieurs francophones natifs, dès 25 $/h. Bureau à 3h de Paris.',
      es: 'Agencia de desarrollo de software para París. SaaS, web, móvil, IA. Ingenieros francófonos, desde 25 $/h.',
      de: 'Softwareentwicklungsagentur für Paris. SaaS, Web, Mobile, KI. Französischsprachige Ingenieure, ab 25 $/h.',
    },
    keywords: ['software development agency Paris', 'agence développement informatique Paris', 'nearshore development France', 'IT outsourcing Paris', 'agence web Paris', 'développeur freelance Paris alternative', 'ESN Paris nearshore'],
    heroTitle: {
      en: 'Software Development Agency for Paris',
      fr: 'Agence de Développement Informatique pour Paris',
      es: 'Agencia de Desarrollo de Software para París',
      de: 'Softwareentwicklung Agentur für Paris',
    },
    heroSubtitle: {
      en: 'Native French-speaking engineers at nearshore rates. Same timezone, same culture, 60% savings vs Parisian agencies. Your extended tech team.',
      fr: 'Ingénieurs francophones natifs à tarifs nearshore. Même fuseau, même culture, 60% d\'économies vs agences parisiennes. Votre équipe tech étendue.',
      es: 'Ingenieros francófonos a precios nearshore. Misma zona horaria, misma cultura, 60% ahorro vs agencias parisinas.',
      de: 'Französischsprachige Ingenieure zu Nearshore-Preisen. Gleiche Zeitzone, 60% Ersparnis vs Pariser Agenturen.',
    },
    whyTitle: {
      en: 'Why Paris Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises Parisiennes Choisissent Call IT Dev',
      es: 'Por Qué las Empresas de París Eligen Call IT Dev',
      de: 'Warum Pariser Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🗣️', title: 'Native French Engineers', description: 'All developers are fluent French speakers. No language barrier in meetings, code reviews, or documentation.' },
      { icon: '⏰', title: 'Identical Timezone (CET)', description: 'Work 9-to-6 Paris time. Real-time standups, pair programming, and instant Slack communication.' },
      { icon: '💰', title: '60% vs Parisian Rates', description: 'Senior devs at €25-40/hr vs €80-120/hr in Paris. Same quality, compliant with French GDPR/CNIL standards.' },
      { icon: '✈️', title: '3h Direct Flight', description: 'Casablanca-Paris CDG: 10+ daily flights on Air France, Royal Air Maroc, Transavia. Easy for kickoffs and sprints.' },
      { icon: '📋', title: 'French Legal Familiarity', description: 'We understand French business culture, contracts, RGPD requirements, and deliver documentation in French.' },
      { icon: '🚀', title: 'Scale Without Hiring Locally', description: 'Skip the 3-month Paris recruitment cycle. Get a dedicated team operational in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do your engineers speak native French?', answer: 'Yes, all engineers assigned to French clients are native French speakers (C2 level minimum). All meetings, documentation, code comments, and communications are in French.' },
      { question: 'How does nearshore compare to hiring in Paris?', answer: 'A senior developer in Paris costs €80-120/hr all-in (salary, social charges, office). Our nearshore rate is €25-40/hr for equivalent skill levels, with the same CET timezone and French-language communication.' },
      { question: 'Can you work with our existing Paris team?', answer: 'Absolutely. We specialize in staff augmentation — our developers integrate into your Jira, Slack, Git workflow. Same standups, same sprints, fully embedded.' },
      { question: 'Do you comply with French data protection (RGPD)?', answer: 'Yes. We are ISO 27001 certified and follow RGPD/GDPR requirements. DPA (convention de traitement) signed. All data handling follows CNIL guidelines.' },
      { question: 'How quickly can you start?', answer: 'Initial team onboarding takes 2 weeks. We can provide CVs and conduct technical interviews within 5 business days. First sprint starts week 3.' },
      { question: 'Do you offer fixed-price projects?', answer: 'Yes. We offer time & materials, fixed-price, and dedicated team models. For fixed-price, we provide a detailed SOW after a 1-2 week discovery phase.' },
    ],
    localRate: '€80-120',
    ourRate: '€25-40',
    savings: '60%+',
  },

  london: {
    slug: 'software-development-agency-london',
    cityName: 'London',
    countryName: 'United Kingdom',
    flag: '🇬🇧',
    timezone: 'GMT/BST',
    currency: 'GBP',
    title: {
      en: 'Software Development Agency London | Call IT Dev',
      fr: 'Agence de Développement Informatique Londres | Call IT Dev',
      de: 'Softwareentwicklung Agentur London | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving London. Custom SaaS, web, mobile, AI. English-speaking engineers, GMT+1 timezone, from £20/hr. 65% savings vs London rates.',
      fr: 'Agence de développement informatique pour Londres. SaaS, web, mobile, IA. Ingénieurs anglophones, dès 20 £/h. 65% d\'économies.',
      de: 'Softwareentwicklungsagentur für London. SaaS, Web, Mobile, KI. Englischsprachige Ingenieure, ab 20 £/h. 65% Ersparnis.',
    },
    keywords: ['software development agency London', 'IT outsourcing London', 'nearshore development UK', 'web development agency London', 'app development London', 'SaaS development UK', 'software house London alternative'],
    heroTitle: {
      en: 'Software Development Agency for London',
      fr: 'Agence de Développement Informatique pour Londres',
      de: 'Softwareentwicklung Agentur für London',
    },
    heroSubtitle: {
      en: 'English-speaking engineers at nearshore rates. 1-hour time difference, 65% savings vs London agencies. Your UK tech team, based in Morocco.',
      fr: 'Ingénieurs anglophones à tarifs nearshore. 1h de décalage horaire, 65% d\'économies vs agences londoniennes.',
      de: 'Englischsprachige Ingenieure zu Nearshore-Preisen. 1h Zeitunterschied, 65% Ersparnis vs Londoner Agenturen.',
    },
    whyTitle: {
      en: 'Why London Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises Londoniennes Choisissent Call IT Dev',
      de: 'Warum Londoner Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🗣️', title: 'Fluent English Engineers', description: 'All developers speak fluent English (C1/C2). Daily standups, code reviews, and documentation in English.' },
      { icon: '⏰', title: '1-Hour Time Difference', description: 'Morocco is GMT+1, just 1 hour ahead of London. Full overlap for real-time collaboration.' },
      { icon: '💰', title: '65% Cost Savings', description: 'Senior devs at £20-35/hr vs £80-150/hr in London. Same quality, UK data protection standards.' },
      { icon: '✈️', title: '3.5h Direct Flight', description: 'Casablanca-London Gatwick/Heathrow: multiple daily flights. Easy for quarterly onsite visits.' },
      { icon: '🛡️', title: 'UK GDPR Compliant', description: 'ISO 27001 certified, UK GDPR compliant, ICO-ready. Enterprise security standards.' },
      { icon: '🚀', title: 'Skip London Hiring Costs', description: 'Avoid £10K+ recruitment fees and 3-month notice periods. Get a dedicated team in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does the time difference work?', answer: 'Morocco is GMT+1, just 1 hour ahead of UK time. We have 7+ hours of daily overlap. Standups, pair programming, and meetings happen in real-time.' },
      { question: 'Do you comply with UK GDPR?', answer: 'Yes. ISO 27001 certified, UK GDPR compliant, and ready for ICO audits. We sign DPAs and follow all UK data protection requirements.' },
      { question: 'What is the cost comparison to London?', answer: 'A senior developer in London costs £80-150/hr (agency) or £60-80K+ salary. Our nearshore rate is £20-35/hr for equivalent expertise, saving 65%+.' },
      { question: 'Can you work with UK startups?', answer: 'Yes! We work with seed-stage to Series C startups. Flexible engagement: start with 2 devs, scale to 20+. No long-term commitments required.' },
      { question: 'How do you handle IP and contracts?', answer: 'All IP belongs to you. We sign comprehensive NDAs, IP assignment agreements, and can work under UK law contracts. Your code, your ownership.' },
      { question: 'What about post-Brexit data transfer?', answer: 'Morocco has an EU adequacy decision for data protection. We follow both UK and EU GDPR standards, ensuring compliant cross-border data handling.' },
    ],
    localRate: '£80-150',
    ourRate: '£20-35',
    savings: '65%+',
  },

  berlin: {
    slug: 'software-development-agency-berlin',
    cityName: 'Berlin',
    countryName: 'Germany',
    flag: '🇩🇪',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Berlin | Call IT Dev',
      fr: 'Agence de Développement Informatique Berlin | Call IT Dev',
      de: 'Softwareentwicklung Agentur Berlin | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Berlin. Custom SaaS, web, mobile, AI. German-speaking engineers, CET timezone, from €25/hr. 55% savings vs Berlin rates.',
      fr: 'Agence de développement informatique pour Berlin. SaaS, web, mobile, IA. Ingénieurs germanophones, dès 25 €/h. 55% d\'économies.',
      de: 'Softwareentwicklungsagentur für Berlin. SaaS, Web, Mobile, KI. Deutschsprachige Ingenieure, CET-Zeitzone, ab 25 €/h. 55% Ersparnis vs Berlin.',
    },
    keywords: ['software development agency Berlin', 'Softwareentwicklung Agentur Berlin', 'IT outsourcing Berlin', 'nearshore Entwicklung Deutschland', 'web development Berlin', 'app Entwicklung Berlin', 'SaaS Entwicklung Deutschland'],
    heroTitle: {
      en: 'Software Development Agency for Berlin',
      fr: 'Agence de Développement Informatique pour Berlin',
      de: 'Softwareentwicklung Agentur für Berlin',
    },
    heroSubtitle: {
      en: 'German-speaking engineers at nearshore rates. Same CET timezone, 55% savings vs Berlin agencies. Your German tech partner.',
      fr: 'Ingénieurs germanophones à tarifs nearshore. Même fuseau CET, 55% d\'économies vs agences berlinoises.',
      de: 'Deutschsprachige Ingenieure zu Nearshore-Preisen. Gleiche CET-Zeitzone, 55% Ersparnis vs Berliner Agenturen. Ihr deutscher Tech-Partner.',
    },
    whyTitle: {
      en: 'Why Berlin Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises Berlinoises Choisissent Call IT Dev',
      de: 'Warum Berliner Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🗣️', title: 'German-Speaking Developers', description: 'Developers fluent in German and English. Meetings, documentation, and communication in your preferred language.' },
      { icon: '⏰', title: 'Same CET Timezone', description: 'Morocco and Berlin share the CET timezone. Full-day overlap for real-time Agile collaboration.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at €25-40/hr vs €70-100/hr in Berlin. DSGVO-compliant, same quality standards.' },
      { icon: '✈️', title: '4h Direct Flight', description: 'Casablanca-Berlin direct flights available. Practical for quarterly workshops and team integration.' },
      { icon: '🛡️', title: 'DSGVO/GDPR Compliant', description: 'ISO 27001 certified, fully DSGVO compliant. German data protection standards met.' },
      { icon: '🏭', title: 'German Engineering Standards', description: 'We apply German engineering rigor: thorough documentation, extensive testing, clean architecture.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Sprechen Ihre Entwickler Deutsch?', answer: 'Ja, wir haben deutschsprachige Entwickler (B2-C1 Niveau). Für Teams, die ausschließlich Deutsch benötigen, stellen wir entsprechende Ressourcen bereit. Alle unsere Entwickler sprechen fließend Englisch.' },
      { question: 'How does nearshore compare to Berlin hiring?', answer: 'A senior developer in Berlin costs €70-100/hr (agency) or €60-80K salary + social costs. Our rate is €25-40/hr for equivalent skills. Same timezone, 55% savings.' },
      { question: 'Are you DSGVO/GDPR compliant?', answer: 'Yes. ISO 27001 certified, fully DSGVO compliant. We sign Auftragsverarbeitungsvertrag (AVV) and follow all German data protection requirements.' },
      { question: 'Can you work with German startups?', answer: 'Yes! Berlin\'s startup ecosystem is our core market. We work with pre-seed to Series B startups. Flexible teams, no long-term lock-in.' },
      { question: 'What is your experience with German companies?', answer: 'We serve 20+ German clients across FinTech, e-commerce, SaaS, and automotive. References available upon request.' },
      { question: 'Do you support SAP integration?', answer: 'Yes. Our team has experience with SAP S/4HANA integration, SAP BTP development, and custom SAP extensions.' },
    ],
    localRate: '€70-100',
    ourRate: '€25-40',
    savings: '55%+',
  },

  madrid: {
    slug: 'software-development-agency-madrid',
    cityName: 'Madrid',
    countryName: 'Spain',
    flag: '🇪🇸',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Madrid | Call IT Dev',
      fr: 'Agence de Développement Informatique Madrid | Call IT Dev',
      es: 'Agencia Desarrollo Software Madrid | Call IT Dev',
      de: 'Softwareentwicklung Agentur Madrid | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Madrid, Spain. Custom SaaS, web, mobile, AI. Spanish-speaking engineers, CET timezone, from €20/hr. 55% savings.',
      fr: 'Agence de développement informatique pour Madrid. SaaS, web, mobile, IA. Ingénieurs hispanophones, dès 20 €/h. 55% d\'économies.',
      es: 'Agencia de desarrollo de software en Madrid. SaaS a medida, web, móvil, IA. Ingenieros hispanohablantes, desde 20 €/h. 55% ahorro vs Madrid.',
      de: 'Softwareentwicklungsagentur für Madrid. SaaS, Web, Mobile, KI. Spanischsprachige Ingenieure, ab 20 €/h.',
    },
    keywords: ['software development agency Madrid', 'agencia desarrollo software Madrid', 'nearshore development Spain', 'IT outsourcing Madrid', 'empresa desarrollo web Madrid', 'SaaS desarrollo España', 'app development Madrid'],
    heroTitle: {
      en: 'Software Development Agency for Madrid',
      fr: 'Agence de Développement Informatique pour Madrid',
      es: 'Agencia de Desarrollo de Software para Madrid',
      de: 'Softwareentwicklung Agentur für Madrid',
    },
    heroSubtitle: {
      en: 'Spanish-speaking engineers at nearshore rates. Same CET timezone, 55% savings vs Madrid agencies. With a physical hub in Madrid.',
      fr: 'Ingénieurs hispanophones à tarifs nearshore. Même fuseau CET, 55% d\'économies vs agences madrilènes.',
      es: 'Ingenieros hispanohablantes a precios nearshore. Misma zona horaria CET, 55% ahorro vs agencias de Madrid. Con oficina en Madrid.',
      de: 'Spanischsprachige Ingenieure zu Nearshore-Preisen. Gleiche CET-Zeitzone, 55% Ersparnis vs Madrider Agenturen.',
    },
    whyTitle: {
      en: 'Why Madrid Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises Madrilènes Choisissent Call IT Dev',
      es: 'Por Qué las Empresas de Madrid Eligen Call IT Dev',
      de: 'Warum Madrider Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🗣️', title: 'Native Spanish Engineers', description: 'All developers speak native Spanish and English. Seamless communication with your Madrid team.' },
      { icon: '🏢', title: 'Madrid Hub', description: 'We operate a physical hub in Madrid for client meetings, workshops, and hybrid collaboration.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at €20-35/hr vs €60-90/hr in Madrid. Same quality, LOPD/GDPR compliant.' },
      { icon: '⏰', title: 'Same CET Timezone', description: 'Casablanca and Madrid share the CET timezone. Zero latency in communication.' },
      { icon: '✈️', title: '1.5h Flight', description: 'Casablanca-Madrid: 1.5-hour flight, 10+ connections weekly. Practically a commute.' },
      { icon: '🌍', title: 'LatAm Market Access', description: 'Spanish-speaking teams also serve your Latin American market expansion needs.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: '¿Tienen oficina en Madrid?', answer: 'Sí, operamos un hub en Madrid para reuniones con clientes, talleres y colaboración híbrida. Nuestro equipo principal de desarrollo está en Casablanca, a solo 1.5 horas de vuelo.' },
      { question: 'How does nearshore compare to Madrid agencies?', answer: 'A senior developer in Madrid costs €60-90/hr (agency). Our nearshore rate is €20-35/hr for equivalent skills. Same timezone, same language, 55% savings.' },
      { question: '¿Cumplen con la LOPD/GDPR?', answer: 'Sí. Certificados ISO 27001, totalmente conformes con LOPD y GDPR. Firmamos contratos de encargado de tratamiento conforme a la normativa española.' },
      { question: 'Can you support LATAM expansion?', answer: 'Yes! Our Spanish-speaking teams serve clients expanding to Mexico, Colombia, Argentina, Chile, and other LATAM markets.' },
      { question: 'What is the Casablanca-Madrid collaboration like?', answer: 'Same timezone (CET), 1.5h flight, cultural proximity. Many of our engineers have studied or worked in Spain. The collaboration is seamless.' },
      { question: '¿Cuál es el tamaño mínimo del equipo?', answer: 'Comenzamos con 2 desarrolladores. Escalamos hasta 30+. Sin compromiso a largo plazo — modelo flexible mensual.' },
    ],
    localRate: '€60-90',
    ourRate: '€20-35',
    savings: '55%+',
  },
};

// Merge extended cities (wave 1-11)
import { CITY_AGENCY_PAGES_EXTENDED } from './city-agency-pages-extended';
import { CITY_AGENCY_PAGES_WAVE2 } from './city-agency-pages-wave2';
import { CITY_AGENCY_PAGES_WAVE3 } from './city-agency-pages-wave3';
import { CITY_AGENCY_PAGES_WAVE4 } from './city-agency-pages-wave4';
import { CITY_AGENCY_PAGES_WAVE5 } from './city-agency-pages-wave5';
import { CITY_AGENCY_PAGES_WAVE6 } from './city-agency-pages-wave6';
import { CITY_AGENCY_PAGES_WAVE7 } from './city-agency-pages-wave7';
import { CITY_AGENCY_PAGES_WAVE8 } from './city-agency-pages-wave8';
import { CITY_AGENCY_PAGES_WAVE9 } from './city-agency-pages-wave9';
import { CITY_AGENCY_PAGES_WAVE10 } from './city-agency-pages-wave10';
import { CITY_AGENCY_PAGES_WAVE11 } from './city-agency-pages-wave11';
import { CITY_AGENCY_PAGES_WAVE12 } from './city-agency-pages-wave12';
import { CITY_AGENCY_PAGES_WAVE13 } from './city-agency-pages-wave13';
import { CITY_AGENCY_PAGES_WAVE14 } from './city-agency-pages-wave14';
import { CITY_AGENCY_PAGES_WAVE15 } from './city-agency-pages-wave15';
import { CITY_AGENCY_PAGES_WAVE16 } from './city-agency-pages-wave16';
import { CITY_AGENCY_PAGES_WAVE17 } from './city-agency-pages-wave17';
import { CITY_AGENCY_PAGES_WAVE18 } from './city-agency-pages-wave18';
import { CITY_AGENCY_PAGES_WAVE19 } from './city-agency-pages-wave19';
import { CITY_AGENCY_PAGES_WAVE20 } from './city-agency-pages-wave20';
import { CITY_AGENCY_PAGES_WAVE21 } from './city-agency-pages-wave21';
import { CITY_AGENCY_PAGES_WAVE22 } from './city-agency-pages-wave22';
import { CITY_AGENCY_PAGES_WAVE23 } from './city-agency-pages-wave23';
import { CITY_AGENCY_PAGES_WAVE24 } from './city-agency-pages-wave24';
import { CITY_AGENCY_PAGES_WAVE25 } from './city-agency-pages-wave25';
import { CITY_AGENCY_PAGES_WAVE26 } from './city-agency-pages-wave26';
import { CITY_AGENCY_PAGES_WAVE27 } from './city-agency-pages-wave27';
import { CITY_AGENCY_PAGES_WAVE28 } from './city-agency-pages-wave28';
import { CITY_AGENCY_PAGES_WAVE29 } from './city-agency-pages-wave29';
import { CITY_AGENCY_PAGES_WAVE30 } from './city-agency-pages-wave30';
import { CITY_AGENCY_PAGES_WAVE31 } from './city-agency-pages-wave31';
import { CITY_AGENCY_PAGES_WAVE32 } from './city-agency-pages-wave32';
import { CITY_AGENCY_PAGES_WAVE33 } from './city-agency-pages-wave33';
import { CITY_AGENCY_PAGES_WAVE34 } from './city-agency-pages-wave34';
import { CITY_AGENCY_PAGES_WAVE35 } from './city-agency-pages-wave35';
import { CITY_AGENCY_PAGES_WAVE36 } from './city-agency-pages-wave36';
import { CITY_AGENCY_PAGES_WAVE37 } from './city-agency-pages-wave37';
import { CITY_AGENCY_PAGES_WAVE38 } from './city-agency-pages-wave38';
import { CITY_AGENCY_PAGES_WAVE39 } from './city-agency-pages-wave39';
import { CITY_AGENCY_PAGES_WAVE40 } from './city-agency-pages-wave40';
Object.assign(CITY_AGENCY_PAGES, CITY_AGENCY_PAGES_EXTENDED, CITY_AGENCY_PAGES_WAVE2, CITY_AGENCY_PAGES_WAVE3, CITY_AGENCY_PAGES_WAVE4, CITY_AGENCY_PAGES_WAVE5, CITY_AGENCY_PAGES_WAVE6, CITY_AGENCY_PAGES_WAVE7, CITY_AGENCY_PAGES_WAVE8, CITY_AGENCY_PAGES_WAVE9, CITY_AGENCY_PAGES_WAVE10, CITY_AGENCY_PAGES_WAVE11, CITY_AGENCY_PAGES_WAVE12, CITY_AGENCY_PAGES_WAVE13, CITY_AGENCY_PAGES_WAVE14, CITY_AGENCY_PAGES_WAVE15, CITY_AGENCY_PAGES_WAVE16, CITY_AGENCY_PAGES_WAVE17, CITY_AGENCY_PAGES_WAVE18, CITY_AGENCY_PAGES_WAVE19, CITY_AGENCY_PAGES_WAVE20, CITY_AGENCY_PAGES_WAVE21, CITY_AGENCY_PAGES_WAVE22, CITY_AGENCY_PAGES_WAVE23, CITY_AGENCY_PAGES_WAVE24, CITY_AGENCY_PAGES_WAVE25, CITY_AGENCY_PAGES_WAVE26, CITY_AGENCY_PAGES_WAVE27, CITY_AGENCY_PAGES_WAVE28, CITY_AGENCY_PAGES_WAVE29, CITY_AGENCY_PAGES_WAVE30, CITY_AGENCY_PAGES_WAVE31, CITY_AGENCY_PAGES_WAVE32, CITY_AGENCY_PAGES_WAVE33, CITY_AGENCY_PAGES_WAVE34, CITY_AGENCY_PAGES_WAVE35, CITY_AGENCY_PAGES_WAVE36, CITY_AGENCY_PAGES_WAVE37, CITY_AGENCY_PAGES_WAVE38, CITY_AGENCY_PAGES_WAVE39, CITY_AGENCY_PAGES_WAVE40);
